import { action, makeObservable, observable, runInAction } from 'mobx';
import { Accreditation, AccreditationDocument } from '../models';
import { APIClient } from '../apiclient/APIClient';
import {
  DeleteAccreditationDocumentResponse,
  FetchAccreditationResponse,
  SubmitAccreditationResponse,
  UploadAccreditationDocumentResponse,
} from '../apiclient/APIClientInterface';

interface AccreditationStateInterface {
  accreditation: Accreditation | null;
  documents: AccreditationDocument[];
  isFetching: boolean;
  isSubmitting: boolean;
  isUploadingDocument: boolean;
  isDeletingDocument: boolean;
  error: string | null;
}

const initialState: AccreditationStateInterface = {
  accreditation: null,
  documents: [],
  isFetching: false,
  isSubmitting: false,
  isUploadingDocument: false,
  isDeletingDocument: false,
  error: null,
};

export class AccreditationStore {
  @observable state: AccreditationStateInterface = { ...initialState };

  private _fetchGeneration = 0;

  constructor() {
    makeObservable(this);
  }

  @action
  fetchAccreditation = async (accountId: string): Promise<FetchAccreditationResponse> => {
    const generation = ++this._fetchGeneration;
    this.state.isFetching = true;
    this.state.error = null;

    const response = await APIClient.fetchAccreditation({ accountId });

    runInAction(() => {
      if (generation !== this._fetchGeneration) return; // stale response after reset
      if (response.success) {
        if (response.accreditation) {
          this.state.accreditation = response.accreditation;
          this.state.documents = response.accreditation.documents ?? [];
        }
        // 204 No Content = no verification record yet, not an error
      } else {
        this.state.error = response.error?.message ?? 'Failed to fetch accreditation.';
      }
      this.state.isFetching = false;
    });

    return response;
  };

  @action
  submitAccreditation = async (accountId: string): Promise<SubmitAccreditationResponse> => {
    const generation = this._fetchGeneration;
    this.state.isSubmitting = true;
    this.state.error = null;

    const response = await APIClient.submitAccreditation({ accountId });

    runInAction(() => {
      if (generation !== this._fetchGeneration) return;
      if (response.success) {
        if (response.accreditation) {
          this.state.accreditation = response.accreditation;
          this.state.documents = response.accreditation.documents ?? [];
        }
      } else {
        this.state.error = response.error?.message ?? 'Failed to submit accreditation.';
      }
      this.state.isSubmitting = false;
    });

    return response;
  };

  @action
  uploadDocument = async (accountId: string, file: File): Promise<UploadAccreditationDocumentResponse> => {
    const generation = this._fetchGeneration;
    this.state.isUploadingDocument = true;
    this.state.error = null;

    const response = await APIClient.uploadAccreditationDocument({ accountId, file });

    runInAction(() => {
      if (generation !== this._fetchGeneration) return;
      if (response.success) {
        if (response.document) {
          this.state.documents = [...this.state.documents, response.document];
        }
      } else {
        this.state.error = response.error?.message ?? 'Failed to upload document.';
      }
      this.state.isUploadingDocument = false;
    });

    return response;
  };

  @action
  deleteDocument = async (accountId: string, documentId: string): Promise<DeleteAccreditationDocumentResponse> => {
    const generation = this._fetchGeneration;
    this.state.isDeletingDocument = true;
    this.state.error = null;

    const response = await APIClient.deleteAccreditationDocument({ accountId, documentId });

    runInAction(() => {
      if (generation !== this._fetchGeneration) return;
      if (response.success) {
        this.state.documents = this.state.documents.filter(doc => String(doc.id) !== documentId);
      } else {
        this.state.error = response.error?.message ?? 'Failed to delete document.';
      }
      this.state.isDeletingDocument = false;
    });

    return response;
  };

  @action
  clearError = () => {
    this.state.error = null;
  };

  @action
  reset = () => {
    this._fetchGeneration++;
    this.state = { ...initialState, documents: [] };
  };
}
